/**
 * Email utility functions for generating mailto URLs and handling email interactions
 *
 * This module provides reusable functions for creating and opening email links
 * with proper URL encoding and UX considerations.
 */

/**
 * Generates a mailto URL with proper encoding for subject and body
 *
 * @param email - The recipient email address
 * @param subject - The email subject line
 * @param body - The email body content
 * @returns A properly formatted mailto URL
 */
export function generateMailtoUrl(
  email: string,
  subject: string,
  body: string
): string {
  const encodedSubject = encodeURIComponent(subject);
  const encodedBody = encodeURIComponent(body);

  return `mailto:${email}?subject=${encodedSubject}&body=${encodedBody}`;
}

/**
 * Opens a mailto link in a new tab/window to preserve the current application context
 *
 * @param email - The recipient email address
 * @param subject - The email subject line
 * @param body - The email body content
 */
export function openMailtoLink(
  email: string,
  subject: string,
  body: string
): void {
  const url = generateMailtoUrl(email, subject, body);
  // Opens in new tab/window, preserving the current application state
  window.open(url, '_blank');
}

/**
 * Generates email content for requesting cancellation check details
 *
 * @param contractNumber - The contract number
 * @param vin - The vehicle identification number
 * @param customerName - The customer name
 * @returns Object containing the email subject and body
 */
export function generateCheckDetailsEmail(
  contractNumber: string,
  vin: string,
  customerName: string
): { subject: string; body: string } {
  const subject = `Cancellation Check Details Request - ${contractNumber}, VIN: ${vin}`;

  const body = `Hello,

I am requesting check details for the following cancelled contract:

Contract Number: ${contractNumber}
VIN: ${vin}
Customer: ${customerName}

Please provide the check details including check number, amount, and payment status.

Thank you.`;

  return { subject, body };
}

/**
 * Generates email content for initiating a cancellation request
 *
 * @param customerName - The customer name
 * @param vehicleVin - The vehicle identification number
 * @returns Object containing the email subject and body
 */
export function generateCancellationRequestEmail(
  customerName: string,
  vehicleVin: string
): { subject: string; body: string } {
  const subject = `Cancellation Request – ${vehicleVin}`;

  const body = `TCA Cancellation Team,

Please initiate cancellation of one or more contracts for the following customer:

● ${customerName}

● ${vehicleVin}

Details of the requested contracts along with supporting documentation has been attached to this email.`;

  return { subject, body };
}

/**
 * Opens a check details email in a new tab/window
 *
 * @param contractNumber - The contract number
 * @param vin - The vehicle identification number
 * @param customerName - The customer name
 */
export function openCheckDetailsEmail(
  contractNumber: string,
  vin: string,
  customerName: string
): void {
  const { subject, body } = generateCheckDetailsEmail(
    contractNumber,
    vin,
    customerName
  );
  openMailtoLink('accountdept@landcar.com', subject, body);
}

/**
 * Opens a cancellation request email in a new tab/window
 *
 * @param customerName - The customer name
 * @param vehicleVin - The vehicle identification number
 */
export function openCancellationRequestEmail(
  customerName: string,
  vehicleVin: string
): void {
  const { subject, body } = generateCancellationRequestEmail(
    customerName,
    vehicleVin
  );
  openMailtoLink('cancellations@landcar.com', subject, body);
}
